import React, { useMemo } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import { useAppSelector } from '@store/hooks';

import { useClaimScrubberUI } from '../../store/hooks';
import { selectScrubberPanel } from '../../store/selectors';
import type { Claim } from '../../types';

import { AnnotationsPanel } from './ClaimScrubber/AnnotationsPanel';
import { groupAnnotations } from './ClaimScrubber/utils/annotationHelpers';
import { mapToFormField } from './ClaimScrubber/utils/fieldMapping';

interface ClaimAnnotationsPanelProps {
  /** The claim with annotations to display */
  claim: Claim | null | undefined;
  /** Optional override for panel position */
  position?: 'bottom' | 'sidebar';
  /** Optional custom styles */
  sx?: SxProps<Theme>;
}

/**
 * Standalone Claim Annotations Panel Component
 * 
 * This component displays claim annotations when the claim is scrubbed.
 * It's a pure display component with no side effects.
 */
export const ClaimAnnotationsPanel: React.FC<ClaimAnnotationsPanelProps> = ({
  claim,
  position,
  sx,
}) => {
  const panel = useAppSelector(selectScrubberPanel);
  const { togglePanel, navigateToField } = useClaimScrubberUI();
  
  // Group annotations if claim has them
  const groupedAnnotations = useMemo(() => {
    if (!claim?.annotations || claim.annotations.length === 0) return null;
    
    return groupAnnotations(
      claim.annotations,
      claim.claim_lines
    );
  }, [claim]);

  // Don't render if no annotations
  if (!groupedAnnotations) {
    return null;
  }

  const handleFieldClick = (fieldPath: string, cmsBox?: string | null) => {
    // Map the API field to form field
    const formField = mapToFormField(fieldPath, claim?.claim_lines);
    if (formField) {
      navigateToField(formField, cmsBox);
    }
  };

  const effectivePosition = position || panel.position;
  const isSidebar = effectivePosition === 'sidebar';

  const mergedSx: SxProps<Theme> = {
    width: '100%',
    height: isSidebar ? '100%' : 'auto',
    ...(sx as object),
  };

  return (
    <Box sx={mergedSx}>
      <AnnotationsPanel
        annotations={groupedAnnotations}
        onFieldClick={handleFieldClick}
        open={panel.isOpen}
        onToggle={togglePanel}
        collapsible={true}
        isSidebar={isSidebar}
      />
    </Box>
  );
};